import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subWeeks,
} from 'date-fns'
import { ICustomerLocalities } from 'src/generated-types/customerLocalities'
import { ILocality } from 'src/generated-types/locality'
import { ISystemReportDto, IUnifiedReportRequest } from 'src/generated-types/system-report-types'

export interface DateRange {
  from: Date | null
  to: Date | null
}

export type DateRangePresetKey = 'thisWeek' | 'lastWeek' | 'previous7Days' | 'thisMonth' | 'thisYear'

export const DATE_RANGE_PRESETS: DateRangePresetKey[] = [
  'thisWeek',
  'lastWeek',
  'previous7Days',
  'thisMonth',
  'thisYear',
]

const WEEK_OPTIONS = { weekStartsOn: 1 as const }

export const getDateRangeForPreset = (preset: DateRangePresetKey, today: Date): DateRange => {
  switch (preset) {
    case 'thisWeek':
      return { from: startOfWeek(today, WEEK_OPTIONS), to: endOfWeek(today, WEEK_OPTIONS) }
    case 'lastWeek': {
      const previousWeek = subWeeks(today, 1)
      return { from: startOfWeek(previousWeek, WEEK_OPTIONS), to: endOfWeek(previousWeek, WEEK_OPTIONS) }
    }
    case 'previous7Days':
      return { from: subDays(today, 6), to: today }
    case 'thisMonth':
      return { from: startOfMonth(today), to: endOfMonth(today) }
    case 'thisYear':
      return { from: startOfYear(today), to: endOfYear(today) }
    default:
      return { from: null, to: null }
  }
}

export interface ScopeLocality {
  id: number
  name: string
}

export interface ScopeGroup {
  id: number
  name: string
  localities: ScopeLocality[]
}

export interface FlattenedScope {
  groups: ScopeGroup[]
  ungrouped: ScopeLocality[]
}

type LocalityCard = ILocality & { groupIdList?: number[] }

export const flattenScope = (data?: ICustomerLocalities): FlattenedScope => {
  if (!data) return { groups: [], ungrouped: [] }

  const cards = (data.localityCards ?? []) as LocalityCard[]
  const groupNameById = new Map<number, string>((data.localityGroups ?? []).map(g => [g.id, g.name]))

  const localitiesByGroupId = new Map<number, ScopeLocality[]>()
  const ungrouped: ScopeLocality[] = []

  for (const card of cards) {
    const scope: ScopeLocality = { id: card.id, name: card.name }
    const groupIds = card.groupIdList ?? []
    if (groupIds.length === 0) {
      ungrouped.push(scope)
    } else {
      for (const gid of groupIds) {
        if (!localitiesByGroupId.has(gid)) localitiesByGroupId.set(gid, [])
        localitiesByGroupId.get(gid)!.push(scope)
      }
    }
  }

  const groups: ScopeGroup[] = (data.localityGroups ?? [])
    .map(group => ({
      id: group.id,
      name: groupNameById.get(group.id) ?? group.name,
      localities: localitiesByGroupId.get(group.id) ?? [],
    }))
    .filter(group => group.localities.length > 0)

  return { groups, ungrouped }
}

export interface ScopeSection {
  key: string
  name: string
  isUngrouped: boolean
  localities: ScopeLocality[]
}

export const buildScopeSections = (scope: FlattenedScope): ScopeSection[] => {
  const groupSections: ScopeSection[] = scope.groups.map(group => ({
    key: `group-${group.id}`,
    name: group.name,
    isUngrouped: false,
    localities: group.localities,
  }))
  if (scope.ungrouped.length === 0) return groupSections
  return [...groupSections, { key: 'ungrouped', name: '', isUngrouped: true, localities: scope.ungrouped }]
}

export const filterSectionsByQuery = (sections: ScopeSection[], query: string): ScopeSection[] => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return sections
  return sections
    .map(section => ({
      ...section,
      localities: section.localities.filter(locality => locality.name.toLowerCase().includes(normalizedQuery)),
    }))
    .filter(section => section.localities.length > 0)
}

export const countSelectedIn = (selectedIds: Set<number>, localities: ScopeLocality[]): number =>
  localities.reduce((count, locality) => (selectedIds.has(locality.id) ? count + 1 : count), 0)

export const getAllLocalityIds = (scope: FlattenedScope): number[] => {
  const uniqueIds = new Set<number>()
  scope.groups.forEach(group => group.localities.forEach(locality => uniqueIds.add(locality.id)))
  scope.ungrouped.forEach(locality => uniqueIds.add(locality.id))
  return Array.from(uniqueIds)
}

export const getUniqueLocalityCount = (scope: FlattenedScope): number => getAllLocalityIds(scope).length

export type GroupSelectionState = 'all' | 'some' | 'none'

export const getGroupSelectionState = (selectedIds: Set<number>, localityIds: number[]): GroupSelectionState => {
  if (localityIds.length === 0) return 'none'
  const selectedCount = localityIds.filter(id => selectedIds.has(id)).length
  if (selectedCount === 0) return 'none'
  return selectedCount === localityIds.length ? 'all' : 'some'
}

export const toggleLocalityId = (selectedIds: Set<number>, id: number): Set<number> => {
  const next = new Set(selectedIds)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  return next
}

export const setGroupSelection = (selectedIds: Set<number>, localityIds: number[], selectAll: boolean): Set<number> => {
  const next = new Set(selectedIds)
  localityIds.forEach(id => (selectAll ? next.add(id) : next.delete(id)))
  return next
}

export const buildReportRequest = (params: {
  customerId: number
  report: ISystemReportDto
  selectedLocalityIds: number[]
  dateRange: DateRange
}): IUnifiedReportRequest => {
  const { customerId, report, selectedLocalityIds, dateRange } = params
  const usesDate = report.usesDateRange === true
  return {
    customerId,
    reportId: report.id,
    localityIds: selectedLocalityIds,
    localityGroupIds: [],
    fromDate: usesDate && dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : null,
    toDate: usesDate && dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : null,
  }
}

export const formatRangeLabel = (range: DateRange): string => {
  if (!range.from || !range.to) return '-'
  return `${format(range.from, 'd MMM yyyy')} - ${format(range.to, 'd MMM yyyy')}`
}

export const isDownloadDisabled = (params: {
  selectedCount: number
  report?: ISystemReportDto
  dateRange: DateRange
}): boolean => {
  const { selectedCount, report, dateRange } = params
  if (!report || selectedCount === 0) return true
  if (report.usesDateRange === true && (!dateRange.from || !dateRange.to)) return true
  return false
}
